import { useState, useEffect, useCallback } from 'react';
import analyticsService from '../../../services/analyticsService';
import './GADashboard.css';

/**
 * Google Analytics Dashboard Component
 * Displays GA4 data including traffic sources, page views, demographics, and real-time users
 */
function GADashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trafficSources, setTrafficSources] = useState([]);
  const [pageViews, setPageViews] = useState([]);
  const [demographics, setDemographics] = useState(null);
  const [realtimeUsers, setRealtimeUsers] = useState(null);
  const [ecommerceData, setEcommerceData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
  });

  // Helper to get default date range (last 30 days)
  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  const fetchGAData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const { startDate, endDate } = dateRange;

      // Fetch all GA data in parallel
      const [
        overviewRes,
        trafficRes,
        pageViewsRes,
        demographicsRes,
        ecommerceRes,
      ] = await Promise.all([
        analyticsService.getGAOverview(startDate, endDate),
        analyticsService.getGATrafficSources(startDate, endDate),
        analyticsService.getGAPageViews(startDate, endDate),
        analyticsService.getGADemographics(startDate, endDate),
        analyticsService.getGAEcommerce(startDate, endDate),
      ]);

      setOverview(overviewRes.data);
      setTrafficSources(overviewRes.data?.data || trafficRes.data?.data || []);
      setPageViews(pageViewsRes.data?.data || []);
      setDemographics(demographicsRes.data?.data || null);
      setEcommerceData(ecommerceRes.data || null);
    } catch (err) {
      console.error('Error fetching GA data:', err);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Fetch real-time users separately (no date range needed)
  const fetchRealtimeUsers = useCallback(async () => {
    try {
      const response = await analyticsService.getGARealtime();
      setRealtimeUsers(response.data);
    } catch (err) {
      console.error('Error fetching realtime users:', err);
    }
  }, []);

  // Fetch data only on initial mount
  useEffect(() => {
    fetchGAData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch real-time data initially and every 30 seconds
    fetchRealtimeUsers();
    const interval = setInterval(fetchRealtimeUsers, 30000);
    return () => clearInterval(interval);
  }, [fetchRealtimeUsers]);

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat('en-GB').format(num);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <div className="ga-dashboard">
        <div className="ga-loading">
          <div className="loading-spinner"></div>
          <span>Loading Google Analytics data...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="ga-dashboard">
        <div className="ga-error-state">
          <div className="ga-error-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3>Failed to Load Analytics</h3>
          <p>Something went wrong. Please try again.</p>
          <button className="btn btn-primary" onClick={fetchGAData}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-dashboard">
      {/* Date Range Picker */}
      <div className="ga-date-range">
        <div className="date-range-inputs">
          <div className="date-input-group">
            <label>From</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              max={dateRange.endDate}
            />
          </div>
          <span className="date-separator">to</span>
          <div className="date-input-group">
            <label>To</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              min={dateRange.startDate}
              max={getDefaultEndDate()}
            />
          </div>
        </div>
        <button className="btn btn-secondary" onClick={fetchGAData}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Real-time Users Card */}
      {realtimeUsers && (
        <div className="ga-realtime-card">
          <div className="realtime-indicator">
            <span className="realtime-dot"></span>
            <span>Real-time</span>
          </div>
          <div className="realtime-value">{formatNumber(realtimeUsers.activeUsers)}</div>
          <div className="realtime-label">Active users right now</div>
          {realtimeUsers.topPages && realtimeUsers.topPages.length > 0 && (
            <div className="realtime-pages">
              <span className="realtime-pages-label">Top pages:</span>
              {realtimeUsers.topPages.slice(0, 3).map((page, index) => (
                <span key={index} className="realtime-page">
                  {page.page} ({page.users})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Overview Stats */}
      {overview && (
        <div className="ga-stats-grid">
          <div className="ga-stat-card">
            <div className="ga-stat-icon users">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{formatNumber(overview.totalUsers)}</div>
              <div className="ga-stat-label">Total Users</div>
            </div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-icon new-users">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{formatNumber(overview.newUsers)}</div>
              <div className="ga-stat-label">New Users</div>
            </div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-icon sessions">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{formatNumber(overview.sessions)}</div>
              <div className="ga-stat-label">Sessions</div>
            </div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-icon bounce">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{overview.bounceRate?.toFixed(1) || 0}%</div>
              <div className="ga-stat-label">Bounce Rate</div>
            </div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-icon duration">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{formatDuration(overview.avgSessionDuration)}</div>
              <div className="ga-stat-label">Avg. Session Duration</div>
            </div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-icon pageviews">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="ga-stat-content">
              <div className="ga-stat-value">{formatNumber(overview.pageViews)}</div>
              <div className="ga-stat-label">Page Views</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="ga-grid">
        {/* Traffic Sources */}
        <div className="ga-card">
          <div className="ga-card-header">
            <h3>Traffic Sources</h3>
          </div>
          <div className="ga-card-body">
            {trafficSources.length > 0 ? (
              <div className="ga-traffic-list">
                {trafficSources.map((source, index) => (
                  <div key={index} className="ga-traffic-item">
                    <div className="ga-traffic-info">
                      <span className="ga-traffic-source">{source.source}</span>
                      <span className="ga-traffic-sessions">{formatNumber(source.sessions)} sessions</span>
                    </div>
                    <div className="ga-traffic-bar-container">
                      <div className="ga-traffic-bar">
                        <div
                          className="ga-traffic-bar-fill"
                          style={{ width: `${source.percentage || 0}%` }}
                        ></div>
                      </div>
                      <span className="ga-traffic-percentage">{source.percentage?.toFixed(1) || 0}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ga-no-data">No traffic data available</div>
            )}
          </div>
        </div>

        {/* Top Pages */}
        <div className="ga-card">
          <div className="ga-card-header">
            <h3>Top Pages</h3>
          </div>
          <div className="ga-card-body">
            {pageViews.length > 0 ? (
              <div className="ga-pages-list">
                {pageViews.slice(0, 5).map((page, index) => (
                  <div key={index} className="ga-page-item">
                    <div className="ga-page-rank">#{index + 1}</div>
                    <div className="ga-page-info">
                      <span className="ga-page-path">{page.page}</span>
                      <span className="ga-page-time">Avg. {formatDuration(page.avgTimeOnPage)}</span>
                    </div>
                    <div className="ga-page-views">{formatNumber(page.views)} views</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ga-no-data">No page view data available</div>
            )}
          </div>
        </div>

        {/* E-commerce Overview */}
        {ecommerceData && (
          <div className="ga-card">
            <div className="ga-card-header">
              <h3>E-commerce Performance</h3>
            </div>
            <div className="ga-card-body">
              <div className="ga-ecommerce-stats">
                <div className="ga-ecommerce-stat">
                  <div className="ga-ecommerce-value">{formatNumber(ecommerceData.transactions)}</div>
                  <div className="ga-ecommerce-label">Transactions</div>
                </div>
                <div className="ga-ecommerce-stat">
                  <div className="ga-ecommerce-value">{formatCurrency(ecommerceData.revenue)}</div>
                  <div className="ga-ecommerce-label">Revenue</div>
                </div>
                <div className="ga-ecommerce-stat">
                  <div className="ga-ecommerce-value">{formatCurrency(ecommerceData.avgOrderValue)}</div>
                  <div className="ga-ecommerce-label">Avg. Order Value</div>
                </div>
                <div className="ga-ecommerce-stat">
                  <div className="ga-ecommerce-value">{ecommerceData.conversionRate?.toFixed(2) || 0}%</div>
                  <div className="ga-ecommerce-label">Conversion Rate</div>
                </div>
              </div>
              {ecommerceData.topProducts && ecommerceData.topProducts.length > 0 && (
                <div className="ga-top-products">
                  <h4>Top Selling Products</h4>
                  {ecommerceData.topProducts.map((product, index) => (
                    <div key={index} className="ga-top-product">
                      <span className="ga-product-name">{product.name}</span>
                      <span className="ga-product-revenue">{formatCurrency(product.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Demographics */}
        {demographics && (
          <div className="ga-card">
            <div className="ga-card-header">
              <h3>User Demographics</h3>
            </div>
            <div className="ga-card-body">
              {demographics.ageGroups && demographics.ageGroups.length > 0 && (
                <div className="ga-demographics-section">
                  <h4>Age Groups</h4>
                  <div className="ga-demographics-bars">
                    {demographics.ageGroups.map((group, index) => (
                      <div key={index} className="ga-demo-bar-item">
                        <span className="ga-demo-label">{group.group}</span>
                        <div className="ga-demo-bar">
                          <div
                            className="ga-demo-bar-fill"
                            style={{ width: `${group.percentage}%` }}
                          ></div>
                        </div>
                        <span className="ga-demo-percentage">{group.percentage?.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {demographics.gender && demographics.gender.length > 0 && (
                <div className="ga-demographics-section">
                  <h4>Gender</h4>
                  <div className="ga-gender-stats">
                    {demographics.gender.map((item, index) => (
                      <div key={index} className="ga-gender-item">
                        <span className="ga-gender-type">{item.type}</span>
                        <span className="ga-gender-percentage">{item.percentage?.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GADashboard;
